import { useState, useEffect } from 'react';
import { Plus, Check, FolderIcon } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Folder } from '@/types/task';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

const COLOR_OPTIONS = [
  '#6366f1', '#3b82f6', '#22c55e', '#f59e0b',
  '#ec4899', '#06b6d4', '#ef4444', '#a855f7',
];

interface FolderSelectorProps {
  folders: Folder[];
  selectedFolderId: string | null;
  onSelect: (folderId: string | null) => void;
  onCreateFolder: (name: string, color: string) => Promise<Folder | null>;
  compact?: boolean;
  /** When used headlessly (no trigger button), set this to open the drawer externally */
  triggerOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FolderSelector({
  folders,
  selectedFolderId,
  onSelect,
  onCreateFolder,
  compact = false,
  triggerOpen,
  onOpenChange,
}: FolderSelectorProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLOR_OPTIONS[0]);

  // Support external trigger
  useEffect(() => {
    if (triggerOpen !== undefined) {
      setOpen(triggerOpen);
    }
  }, [triggerOpen]);

  const selectedFolder = folders.find(f => f.id === selectedFolderId);

  const handleSelect = (folderId: string) => {
    const nextId = selectedFolderId === folderId ? null : folderId;
    handleOpenChange(false);
    queueMicrotask(() => onSelect(nextId));
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const folder = await onCreateFolder(newName.trim(), newColor);
    if (folder) {
      setNewName('');
      setShowCreate(false);
      handleOpenChange(false);
      queueMicrotask(() => onSelect(folder.id));
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    onOpenChange?.(isOpen);
    if (!isOpen) {
      setShowCreate(false);
      setNewName('');
    }
  };

  // Headless mode: no trigger button, just the drawer
  const isHeadless = triggerOpen !== undefined;

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      {!isHeadless && (
        <DrawerTrigger asChild>
          {selectedFolder ? (
            <button type="button" className="focus:outline-none">
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                  compact ? "text-[10px] px-1.5" : "text-xs"
                )}
                style={{
                  backgroundColor: `${selectedFolder.color}20`,
                  color: selectedFolder.color,
                }}
              >
                <FolderIcon className="h-2.5 w-2.5" />
                {selectedFolder.name}
              </span>
            </button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                "h-6 px-2 text-muted-foreground hover:text-foreground",
                compact && "h-5 px-1.5"
              )}
            >
              <FolderIcon className="h-3 w-3 mr-1" />
              <span className="text-xs">Folder</span>
            </Button>
          )}
        </DrawerTrigger>
      )}
      <DrawerContent>
        <DrawerHeader className="text-center pb-2">
          <DrawerTitle>{showCreate ? 'Create Folder' : 'Select Folder'}</DrawerTitle>
        </DrawerHeader>

        <div className="px-0 pb-4">
          {showCreate ? (
            <div className="space-y-4">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Folder name..."
                className="h-11 text-base"
                autoFocus={!isMobile}
              />
              <div className="flex flex-wrap gap-2 justify-center">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewColor(color)}
                    className={cn(
                      "w-8 h-8 rounded-full transition-all",
                      newColor === color && "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 h-11" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button className="flex-1 h-11" onClick={handleCreate} disabled={!newName.trim()}>Create</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedFolderId && (
                <button
                  type="button"
                  onClick={() => { handleOpenChange(false); queueMicrotask(() => onSelect(null)); }}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:bg-accent rounded-xl transition-colors"
                >
                  Clear folder
                </button>
              )}
              <div className="grid grid-cols-2 gap-2">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => handleSelect(folder.id)}
                    className={cn(
                      "flex items-center justify-between px-4 py-3 rounded-xl transition-colors",
                      selectedFolderId === folder.id ? "bg-accent" : "bg-muted/50 hover:bg-accent/50"
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: folder.color }} />
                      <span className="text-sm font-medium">{folder.name}</span>
                    </span>
                    {selectedFolderId === folder.id && <Check className="h-4 w-4 text-primary" />}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:bg-accent rounded-xl transition-colors mt-3 border border-dashed border-border"
              >
                <Plus className="h-4 w-4" />
                Create new folder
              </button>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
